import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, distinctUntilChanged, filter } from 'rxjs/operators';
import { AppServices } from '../../servicelayer/app-services';
import { appRouteConstants } from '../../app.route.constants';
import { Role, ISession } from 'portalServices';

@Injectable({
    providedIn: 'root'
})
export class RouteAuthenticationService {
    private isInitialized = new BehaviorSubject<boolean>(false);
    public isAuthenticated$: Observable<boolean>;
    private lastIntentionalNavigation = '';
    private authTrackingInitialized = false;

    constructor(
        private appServices: AppServices,
        private router: Router
    ) {
        // Don't initialize immediately for join routes and login routes to prevent getProfile calls
        const currentUrl = this.router.url;
        console.log('RouteAuthenticationService: Initializing for URL:', currentUrl);
        
        if (!currentUrl.includes('join') && !currentUrl.includes('login')) {
            console.log('RouteAuthenticationService: Initializing authentication tracking for protected route');
            this.initialize();
        } else {
            console.log('RouteAuthenticationService: Skipping automatic authentication tracking for public route to prevent getProfile calls');
            // For public routes, create a simple observable that always emits false initially
            this.isAuthenticated$ = new BehaviorSubject<boolean>(false).asObservable();
            this.isInitialized.next(true);
        }
        
        this.trackNavigations();
    }

    public get authenticationService() {
        return this.appServices.authenticationService;
    }

    private initialize(): void {
        if (this.authTrackingInitialized) {
            console.log('RouteAuthenticationService: Authentication tracking already initialized');
            return;
        }
        
        this.authTrackingInitialized = true;
        console.log('RouteAuthenticationService: Initializing authentication tracking');
        
        // Create an observable that emits authentication status
        this.isAuthenticated$ = this.authenticationService.userSession.pipe(
            map((session: ISession) => session?.loggedin || false),
            distinctUntilChanged()
        );

        // Subscribe to authentication state changes
        this.isAuthenticated$.subscribe(isAuthenticated => {
            this.isInitialized.next(true);
            console.log('RouteAuthenticationService: Authentication state changed:', isAuthenticated);
        });
    }

    /**
     * Initialize authentication tracking if not already done (called when navigating to protected routes)
     */
    public initializeAuthenticationTracking(): void {
        if (!this.authTrackingInitialized) {
            console.log('RouteAuthenticationService: Initializing authentication tracking on demand');
            this.initialize();
        }
    }

    private trackNavigations(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(event => {
            const navigationEnd = event as NavigationEnd;
            this.lastIntentionalNavigation = navigationEnd.url;
            console.log('RouteAuthenticationService: Tracked navigation to:', navigationEnd.url);
        });
    }

    /**
     * Check if user is authenticated and has required roles for route
     */
    canAccessRoute(requiredRoles?: Role[]): boolean {
        if (!this.authenticationService.isAuthenticated()) {
            console.log('RouteAuthenticationService: User not authenticated');
            return false;
        }

        if (!requiredRoles || requiredRoles.length === 0) {
            console.log('RouteAuthenticationService: No roles required, allowing access');
            return true;
        }

        const hasRequiredRole = requiredRoles.some(role => 
            this.authenticationService.hasRole(role)
        );

        if (!hasRequiredRole) {
            console.log('RouteAuthenticationService: User lacks required roles');
            console.log('Required roles:', requiredRoles);
            console.log('User roles:', this.getUserRoles());
            return false;
        }

        console.log('RouteAuthenticationService: User has required roles, allowing access');
        return true;
    }

    /**
     * Handle authentication failure by redirecting to appropriate route
     */
    handleAuthenticationFailure(currentUrl: string, requiredRoles?: Role[]): void {
        if (!this.authenticationService.isAuthenticated()) {
            console.log('RouteAuthenticationService: Redirecting to login - user not authenticated');
            this.router.navigate([appRouteConstants.AUTH.LOGIN], {
                queryParams: { returnUrl: currentUrl }
            });
        } else if (requiredRoles && requiredRoles.length > 0) {
            console.log('RouteAuthenticationService: Redirecting to dashboard - insufficient permissions');
            this.router.navigate([appRouteConstants.DASHBOARD]);
        }
    }

    /**
     * Check route on refresh and ensure proper authentication
     */
    validateRouteOnRefresh(currentUrl: string, requiredRoles?: Role[]): Observable<boolean> {
        return new Observable(observer => {
            // Wait for initialization to complete
            this.isInitialized.subscribe(initialized => {
                if (initialized) {
                    const canAccess = this.canAccessRoute(requiredRoles);
                    if (!canAccess) {
                        this.handleAuthenticationFailure(currentUrl, requiredRoles);
                    }
                    observer.next(canAccess);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get user roles for debugging - accessing roles through the session
     */
    getUserRoles(): Role[] {
        try {
            // Access session roles through the authentication service's session property
            const session = (this.authenticationService as any).session;
            return session?.roles || [];
        } catch (error) {
            console.warn('RouteAuthenticationService: Could not get user roles:', error);
            return [];
        }
    }

    /**
     * Check if user has specific role
     */
    hasRole(role: Role): boolean {
        return this.authenticationService.hasRole(role);
    }

    /**
     * Check if user can access any of the provided roles
     */
    hasAnyRole(roles: Role[]): boolean {
        return roles.some(role => this.authenticationService.hasRole(role));
    }

    /**
     * Check if user has all of the provided roles
     */
    hasAllRoles(roles: Role[]): boolean {
        return roles.every(role => this.authenticationService.hasRole(role));
    }

    /**
     * Get the last intentional navigation URL
     */
    getLastIntentionalNavigation(): string {
        return this.lastIntentionalNavigation;
    }

    /**
     * Check if the current navigation is intentional (not a refresh)
     */
    isIntentionalNavigation(url: string): boolean {
        return this.lastIntentionalNavigation === url;
    }
}